import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { formatSelectOptions } from 'src/app/utils/formatSelectOptions';

import { marketplaceApi } from '../api/marketplaceApi';
import {
  MarketplaceOfferSelectData,
  MarketplaceOffersParams,
  MarketplacePropertiesResponse,
} from '../types/types';
import { mapOfferToSelectData } from '../utils/marketplaceOfferOptions.utils';

export function useMarketplaceCategories() {
  const { data, isLoading } = useQuery({
    queryKey: ['marketplace-categories'],
    queryFn: marketplaceApi.fetchCategories,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const options = formatSelectOptions(data, {
    label: 'name',
    value: 'id',
  });

  return { options, isLoading };
}

export function useBannerCategories() {
  const { data, isLoading } = useQuery({
    queryKey: ['marketplace-categories'],
    queryFn: marketplaceApi.fetchCategories,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const options = (data ?? []).map(category => ({
    label: category.name,
    value: String(category.id),
  }));

  return { options, isLoading };
}

export function useBannerProducts() {
  const { data, isLoading } = useQuery({
    queryKey: ['marketplace-products-list'],
    queryFn: marketplaceApi.fetchProductsList,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const options = (data ?? []).map(product => ({
    label: product.title,
    value: String(product.id),
  }));

  return { options, isLoading };
}

export function useMarketplaceLanes() {
  const { data, isLoading } = useQuery({
    queryKey: ['marketplace-lanes'],
    queryFn: marketplaceApi.fetchLanes,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const options = formatSelectOptions(data, {
    label: 'name',
    value: 'id',
  });

  return { options, isLoading };
}

export function useMarketplaceProperties(): {
  properties: MarketplacePropertiesResponse | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ['marketplace-properties'],
    queryFn: marketplaceApi.fetchProperties,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

  return { properties: data, isLoading };
}

export function useMarketplaceOffers(
  params: MarketplaceOffersParams,
  enabled = true
) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['marketplace-offers-v2', params],
    queryFn: () => marketplaceApi.fetchOffers(params),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  return {
    offers: data?.data ?? [],
    total: data?.total ?? 0,
    isLoading,
    isError,
  };
}

interface UseMarketplaceOfferSelectOptionsParams {
  search?: string;
  typeId?: number;
  acquisitionId?: number;
}

type MarketplaceOfferSelectOption = {
  value: string;
  label: string;
  searchText: string;
  offer: MarketplaceOfferSelectData;
};

export function useMarketplaceOfferSelectOptions({
  search,
  typeId,
  acquisitionId,
}: UseMarketplaceOfferSelectOptionsParams) {
  const { properties } = useMarketplaceProperties();

  const {
    offers,
    isLoading: isLoadingOffers,
    isError: hasOffersError,
  } = useMarketplaceOffers(
    {
      search: search?.trim() || undefined,
      status: ['active', 'scheduled'],
      ...(typeId !== undefined && { typeId }),
      ...(acquisitionId !== undefined && { acquisitionId }),
    },
    true
  );

  const options = useMemo(
    () => offers.map(offer => mapOfferToSelectData(offer, properties)),
    [offers, properties]
  );

  const selectOptions = useMemo(
    () =>
      formatSelectOptions<
        MarketplaceOfferSelectData,
        { searchText: string; offer: MarketplaceOfferSelectData }
      >(options, {
        value: 'offerId',
        label: 'name',
        extra: offer => ({
          searchText: `${offer.name} ${offer.offerId}`,
          offer,
        }),
      }) as MarketplaceOfferSelectOption[],
    [options]
  );

  return {
    options,
    selectOptions,
    isLoading: isLoadingOffers,
    hasError: hasOffersError,
  };
}
